import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import {
  DollarSign, Plus, TrendingDown, TrendingUp, Wallet,
  Check, X, Pencil, Trash2, AlertTriangle, Upload, Eye, EyeOff, Search,
  ArrowRightLeft, Copy
} from 'lucide-react';
import { formatCurrency } from '../utils/invoiceData';
import {
  depositCategories, getAccountLabel, getDepositCategoryLabel, findDuplicateIdSet,
  findExactDuplicateIdsToDelete
} from '../utils/expenseData';
import { buildCategoryLookup } from './ExpenseCategories';
import { startOfMonth, endOfMonth, subMonths, startOfYear, isWithinInterval } from 'date-fns';
import './Expenses.css';

const toDate = (val) => {
  if (!val) return null;
  if (val instanceof Date) return val;
  if (typeof val === 'string') {
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof val.toDate === 'function') return val.toDate();
  if (typeof val === 'number') return new Date(val);
  return null;
};

const emptyExpense = {
  description: '',
  amount: '',
  categoryId: '',
  accountId: '',
  dueDate: new Date().toISOString().split('T')[0],
  paid: false,
  notes: ''
};

const emptyDeposit = {
  description: '',
  amount: '',
  category: 'cleaning_service',
  accountId: '',
  date: new Date().toISOString().split('T')[0],
  notes: ''
};

const emptyTransfer = {
  description: '',
  amount: '',
  fromAccountId: '',
  toAccountId: '',
  date: new Date().toISOString().split('T')[0],
  notes: ''
};

// Returns the best display label for an expense's category.
// Prefers the new categoryId -> expenseCategories lookup, falls back to the
// legacy `account` string (hardcoded labels) so old records still read cleanly.
export const getExpenseCategoryDisplay = (expense, categoryLookup) => {
  if (expense.categoryId && categoryLookup[expense.categoryId]) {
    return categoryLookup[expense.categoryId].name;
  }
  if (expense.account) return getAccountLabel(expense.account);
  return 'Uncategorized';
};

function Expenses() {
  const {
    state,
    addExpense, updateExpense, deleteExpense,
    addDeposit, updateDeposit, deleteDeposit,
    addTransfer, updateTransfer, deleteTransfer
  } = useApp();
  const [activeTab, setActiveTab] = useState('expenses');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('expense');
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyExpense);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [dateRange, setDateRange] = useState('this_month');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterAccountId, setFilterAccountId] = useState('all');
  const [filterPaid, setFilterPaid] = useState('all');
  const [filterReviewed, setFilterReviewed] = useState('all');
  const [filterDuplicates, setFilterDuplicates] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const [selectedExpenseIds, setSelectedExpenseIds] = useState({});
  const [selectedDepositIds, setSelectedDepositIds] = useState({});
  const [bulkCategoryId, setBulkCategoryId] = useState('');
  const [bulkDepositCategory, setBulkDepositCategory] = useState('');
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const [removingDuplicates, setRemovingDuplicates] = useState(false);

  useEffect(() => {
    setSelectedExpenseIds({});
    setSelectedDepositIds({});
    setBulkCategoryId('');
    setBulkDepositCategory('');
    setBulkError('');
  }, [activeTab]);

  const activeAccounts = useMemo(
    () => (state.accounts || []).filter(a => !a.archived).sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [state.accounts]
  );
  const accountLookup = useMemo(() => {
    const map = {};
    (state.accounts || []).forEach(a => { map[a.id] = a; });
    return map;
  }, [state.accounts]);

  const categories = useMemo(() => state.expenseCategories || [], [state.expenseCategories]);
  const categoryLookup = useMemo(() => buildCategoryLookup(categories), [categories]);
  // Categories sorted with parents then their children, for the dropdown
  const categoryOptions = useMemo(() => {
    const parents = categories.filter(c => !c.parentId).sort((a, b) => a.name.localeCompare(b.name));
    const childrenByParent = {};
    categories.forEach(c => {
      if (c.parentId) (childrenByParent[c.parentId] = childrenByParent[c.parentId] || []).push(c);
    });
    Object.keys(childrenByParent).forEach(k =>
      childrenByParent[k].sort((a, b) => a.name.localeCompare(b.name))
    );
    const result = [];
    parents.forEach(p => {
      result.push({ id: p.id, label: p.name, isParent: true });
      (childrenByParent[p.id] || []).forEach(child => {
        result.push({ id: child.id, label: `— ${child.name}`, isParent: false });
      });
    });
    // Orphans
    categories
      .filter(c => c.parentId && !categoryLookup[c.parentId])
      .forEach(orphan => result.push({ id: orphan.id, label: orphan.name, isParent: false }));
    return result;
  }, [categories, categoryLookup]);

  const dateBounds = useMemo(() => {
    const today = new Date();
    switch (dateRange) {
      case 'this_month':
        return { start: startOfMonth(today), end: endOfMonth(today) };
      case 'last_month': {
        const lm = subMonths(today, 1);
        return { start: startOfMonth(lm), end: endOfMonth(lm) };
      }
      case 'last_3_months':
        return { start: startOfMonth(subMonths(today, 2)), end: endOfMonth(today) };
      case 'year_to_date':
        return { start: startOfYear(today), end: today };
      case 'custom':
        return {
          start: customStart ? new Date(customStart + 'T00:00:00') : today,
          end: customEnd ? new Date(customEnd + 'T23:59:59') : today
        };
      default:
        return { start: startOfMonth(today), end: endOfMonth(today) };
    }
  }, [dateRange, customStart, customEnd]);

  const normalizedSearch = searchQuery.trim().toLowerCase();
  const matchesSearch = (item) => {
    if (!normalizedSearch) return true;
    return (
      (item.description || '').toLowerCase().includes(normalizedSearch) ||
      (item.notes || '').toLowerCase().includes(normalizedSearch)
    );
  };

  // Duplicate detection runs over ALL records (not just the filtered period)
  // so a charge duplicated across two months still flags in both views.
  const expenseDuplicateIds = useMemo(() => findDuplicateIdSet(state.expenses || [], {
    getAmount: e => e.amount,
    getBucketKey: e => e.accountId || '',
    getDate: e => e.dueDate
  }), [state.expenses]);

  const depositDuplicateIds = useMemo(() => findDuplicateIdSet(state.deposits || [], {
    getAmount: d => d.amount,
    getBucketKey: d => d.accountId || '',
    getDate: d => d.date
  }), [state.deposits]);

  const transferDuplicateIds = useMemo(() => findDuplicateIdSet(state.transfers || [], {
    getAmount: t => t.amount,
    getBucketKey: t => `${t.fromAccountId || ''}>${t.toAccountId || ''}`,
    getDate: t => t.date
  }), [state.transfers]);

  // IDs of EXACT duplicates to delete — records where description, notes,
  // amount, and date all match. For each matching group we keep the oldest.
  const expenseExactDupIdsToDelete = useMemo(
    () => findExactDuplicateIdsToDelete(state.expenses || [], { getDate: e => e.dueDate }),
    [state.expenses]
  );
  const depositExactDupIdsToDelete = useMemo(
    () => findExactDuplicateIdsToDelete(state.deposits || [], { getDate: d => d.date }),
    [state.deposits]
  );

  const expenses = useMemo(() => {
    return (state.expenses || []).filter(e => {
      const d = toDate(e.dueDate);
      if (!d) return false;
      if (!isWithinInterval(d, dateBounds)) return false;
      if (filterCategory !== 'all') {
        const effectiveCat = e.categoryId || e.account;
        if (effectiveCat !== filterCategory) return false;
      }
      if (filterAccountId !== 'all' && (e.accountId || '') !== filterAccountId) return false;
      if (filterPaid === 'paid' && !e.paid) return false;
      if (filterPaid === 'unpaid' && e.paid) return false;
      if (filterReviewed === 'reviewed' && !e.reviewed) return false;
      if (filterReviewed === 'unreviewed' && e.reviewed) return false;
      if (filterDuplicates === 'only' && !expenseDuplicateIds.has(e.id)) return false;
      if (!matchesSearch(e)) return false;
      return true;
    }).sort((a, b) => new Date(b.dueDate) - new Date(a.dueDate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.expenses, dateBounds, filterCategory, filterAccountId, filterPaid, filterReviewed, filterDuplicates, expenseDuplicateIds, normalizedSearch]);

  const deposits = useMemo(() => {
    return (state.deposits || []).filter(d => {
      const dt = toDate(d.date);
      if (!dt) return false;
      if (!isWithinInterval(dt, dateBounds)) return false;
      if (filterAccountId !== 'all' && (d.accountId || '') !== filterAccountId) return false;
      if (filterReviewed === 'reviewed' && !d.reviewed) return false;
      if (filterReviewed === 'unreviewed' && d.reviewed) return false;
      if (filterDuplicates === 'only' && !depositDuplicateIds.has(d.id)) return false;
      if (!matchesSearch(d)) return false;
      return true;
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.deposits, dateBounds, filterAccountId, filterReviewed, filterDuplicates, depositDuplicateIds, normalizedSearch]);

  const transfers = useMemo(() => {
    return (state.transfers || []).filter(t => {
      const dt = toDate(t.date);
      if (!dt) return false;
      if (!isWithinInterval(dt, dateBounds)) return false;
      if (filterAccountId !== 'all') {
        if ((t.fromAccountId || '') !== filterAccountId && (t.toAccountId || '') !== filterAccountId) return false;
      }
      if (filterReviewed === 'reviewed' && !t.reviewed) return false;
      if (filterReviewed === 'unreviewed' && t.reviewed) return false;
      if (filterDuplicates === 'only' && !transferDuplicateIds.has(t.id)) return false;
      if (!matchesSearch(t)) return false;
      return true;
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.transfers, dateBounds, filterAccountId, filterReviewed, filterDuplicates, transferDuplicateIds, normalizedSearch]);

  const totalExpenses = expenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const unpaidExpenses = expenses.filter(e => !e.paid).reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const totalDeposits = deposits.reduce((s, d) => s + (Number(d.amount) || 0), 0);
  const totalTransfers = transfers.reduce((s, t) => s + (Number(t.amount) || 0), 0);
  const netIncome = totalDeposits - totalExpenses;

  const visibleExpenseDupCount = expenses.filter(e => expenseDuplicateIds.has(e.id)).length;
  const visibleDepositDupCount = deposits.filter(d => depositDuplicateIds.has(d.id)).length;
  const visibleTransferDupCount = transfers.filter(t => transferDuplicateIds.has(t.id)).length;

  const overdueCount = expenses.filter(e => {
    if (e.paid) return false;
    const due = toDate(e.dueDate);
    return due && due < new Date();
  }).length;

  const emptyForType = (type) => {
    if (type === 'expense') return emptyExpense;
    if (type === 'transfer') return emptyTransfer;
    return emptyDeposit;
  };

  const openAddModal = (type) => {
    setModalType(type);
    setEditingItem(null);
    setFormData({ ...emptyForType(type) });
    setSaveError('');
    setShowModal(true);
  };

  const openEditModal = (item, type) => {
    setModalType(type);
    setEditingItem(item);
    setFormData({ ...emptyForType(type), ...item, amount: String(item.amount) });
    setSaveError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSaveError('');
    setSaving(false);
  };

  const handleSave = async () => {
    const data = { ...formData, amount: parseFloat(formData.amount) || 0 };
    if (!data.amount) return;
    if (modalType !== 'transfer' && !data.description) return;
    if (modalType === 'transfer') {
      if (!data.fromAccountId || !data.toAccountId) {
        setSaveError('Pick both a source and a destination account.');
        return;
      }
      if (data.fromAccountId === data.toAccountId) {
        setSaveError('Source and destination accounts must be different.');
        return;
      }
    }

    setSaveError('');
    setSaving(true);
    try {
      if (modalType === 'expense') {
        // Store categoryId (new) and leave legacy `account` alone. If editing a legacy
        // record with only `account` and no categoryId is picked, leave it as-is.
        if (editingItem) {
          await updateExpense(editingItem.id, data);
        } else {
          await addExpense(data);
        }
      } else if (modalType === 'transfer') {
        if (editingItem) {
          await updateTransfer(editingItem.id, data);
        } else {
          await addTransfer(data);
        }
      } else {
        if (editingItem) {
          await updateDeposit(editingItem.id, data);
        } else {
          await addDeposit(data);
        }
      }
      closeModal();
    } catch (err) {
      setSaveError(err?.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkPaid = async (expense) => {
    await updateExpense(expense.id, { paid: true, paidDate: new Date().toISOString() });
  };

  const handleMarkUnpaid = async (expense) => {
    await updateExpense(expense.id, { paid: false, paidDate: null });
  };

  const handleToggleReviewed = async (item, type) => {
    const reviewed = !item.reviewed;
    const patch = { reviewed, reviewedAt: reviewed ? new Date().toISOString() : null };
    try {
      if (type === 'expense') {
        await updateExpense(item.id, patch);
      } else if (type === 'transfer') {
        await updateTransfer(item.id, patch);
      } else {
        await updateDeposit(item.id, patch);
      }
    } catch (err) {
      window.alert(err?.message || 'Failed to update review status.');
    }
  };

  const handleDelete = async (id, type) => {
    try {
      if (type === 'expense') {
        await deleteExpense(id);
      } else if (type === 'transfer') {
        await deleteTransfer(id);
      } else {
        await deleteDeposit(id);
      }
    } catch (err) {
      window.alert(err?.message || 'Failed to delete. Please try again.');
    }
  };

  const handleRemoveExactDuplicates = async (type) => {
    const ids = type === 'expense'
      ? expenseExactDupIdsToDelete
      : depositExactDupIdsToDelete;
    if (ids.length === 0) return;
    const label = type === 'expense' ? 'expense' : 'deposit';
    const plural = ids.length === 1 ? '' : 's';
    const ok = window.confirm(
      `Delete ${ids.length} duplicate ${label}${plural}? ` +
      `Records with the same description, notes, amount, and date will be ` +
      `collapsed to one (the oldest is kept). This cannot be undone.`
    );
    if (!ok) return;
    setRemovingDuplicates(true);
    const failed = [];
    try {
      for (const id of ids) {
        try {
          if (type === 'expense') {
            await deleteExpense(id);
          } else {
            await deleteDeposit(id);
          }
        } catch (err) {
          failed.push(id);
        }
      }
      if (failed.length > 0) {
        window.alert(`Removed ${ids.length - failed.length} duplicate${plural}. ${failed.length} failed to delete.`);
      }
    } finally {
      setRemovingDuplicates(false);
    }
  };

  const isOverdue = (expense) => {
    if (expense.paid) return false;
    const due = toDate(expense.dueDate);
    return due && due < new Date();
  };

  const accountName = (id) => (id && accountLookup[id]?.name) || '—';

  const selectedExpenseCount = Object.keys(selectedExpenseIds).length;
  const selectedDepositCount = Object.keys(selectedDepositIds).length;
  const allExpensesSelected = expenses.length > 0 && expenses.every(e => selectedExpenseIds[e.id]);
  const allDepositsSelected = deposits.length > 0 && deposits.every(d => selectedDepositIds[d.id]);

  const toggleExpenseSelection = (id) => {
    setSelectedExpenseIds(prev => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = true;
      return next;
    });
  };

  const toggleDepositSelection = (id) => {
    setSelectedDepositIds(prev => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = true;
      return next;
    });
  };

  const toggleAllExpenses = () => {
    if (allExpensesSelected) {
      setSelectedExpenseIds({});
    } else {
      const next = {};
      expenses.forEach(e => { next[e.id] = true; });
      setSelectedExpenseIds(next);
    }
  };

  const toggleAllDeposits = () => {
    if (allDepositsSelected) {
      setSelectedDepositIds({});
    } else {
      const next = {};
      deposits.forEach(d => { next[d.id] = true; });
      setSelectedDepositIds(next);
    }
  };

  const applyBulkExpenseCategory = async () => {
    const ids = Object.keys(selectedExpenseIds);
    if (ids.length === 0) return;
    setBulkError('');
    setBulkApplying(true);
    try {
      for (const id of ids) {
        await updateExpense(id, { categoryId: bulkCategoryId });
      }
      setSelectedExpenseIds({});
      setBulkCategoryId('');
    } catch (err) {
      setBulkError(err?.message || 'Failed to update. Please try again.');
    } finally {
      setBulkApplying(false);
    }
  };

  const applyBulkDepositCategory = async () => {
    const ids = Object.keys(selectedDepositIds);
    if (ids.length === 0 || !bulkDepositCategory) return;
    setBulkError('');
    setBulkApplying(true);
    try {
      for (const id of ids) {
        await updateDeposit(id, { category: bulkDepositCategory });
      }
      setSelectedDepositIds({});
      setBulkDepositCategory('');
    } catch (err) {
      setBulkError(err?.message || 'Failed to update. Please try again.');
    } finally {
      setBulkApplying(false);
    }
  };

  return (
    <div className="expenses-page">
      <div className="expenses-header">
        <div className="header-title">
          <h1>Expenses & Income</h1>
          <div className="subtitle">Track business expenses, deposits, and sales</div>
        </div>
        <div className="header-actions">
          <Link className="btn-secondary" to="/admin/accounts">
            <Wallet size={16} /> Accounts
          </Link>
          <Link className="btn-secondary" to="/admin/expense-categories">
            <DollarSign size={16} /> Categories
          </Link>
          <Link className="btn-secondary" to="/admin/bank-import">
            <Upload size={16} /> Import Bank Statement
          </Link>
          <button className="btn-secondary" onClick={() => openAddModal('transfer')}>
            <ArrowRightLeft size={16} /> Record Transfer
          </button>
          <button className="btn-secondary" onClick={() => openAddModal('deposit')}>
            <TrendingUp size={16} /> Add Deposit / Sale
          </button>
          <button className="btn-primary" onClick={() => openAddModal('expense')}>
            <Plus size={16} /> Add Expense
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="expenses-summary">
        <div className="summary-card">
          <div className="card-icon"><TrendingDown size={22} /></div>
          <div className="card-content">
            <div className="card-value">{formatCurrency(totalExpenses)}</div>
            <div className="card-label">Total Expenses</div>
          </div>
        </div>
        <div className={`summary-card ${unpaidExpenses > 0 ? 'warning' : ''}`}>
          <div className="card-icon"><AlertTriangle size={22} /></div>
          <div className="card-content">
            <div className="card-value">{formatCurrency(unpaidExpenses)}</div>
            <div className="card-label">Unpaid ({overdueCount} overdue)</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon"><TrendingUp size={22} /></div>
          <div className="card-content">
            <div className="card-value">{formatCurrency(totalDeposits)}</div>
            <div className="card-label">Total Deposits / Sales</div>
          </div>
        </div>
        <div className={`summary-card highlight`}>
          <div className="card-icon"><Wallet size={22} /></div>
          <div className="card-content">
            <div className="card-value">{formatCurrency(netIncome)}</div>
            <div className="card-label">Net Income</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="expenses-tabs">
        <button className={`tab-btn ${activeTab === 'expenses' ? 'active' : ''}`} onClick={() => setActiveTab('expenses')}>
          Expenses ({expenses.length})
        </button>
        <button className={`tab-btn ${activeTab === 'deposits' ? 'active' : ''}`} onClick={() => setActiveTab('deposits')}>
          Deposits / Sales ({deposits.length})
        </button>
        <button className={`tab-btn ${activeTab === 'transfers' ? 'active' : ''}`} onClick={() => setActiveTab('transfers')}>
          Transfers ({transfers.length})
        </button>
      </div>

      {/* Filters */}
      <div className="expenses-filters">
        <div className="filter-group search-group">
          <label>Search</label>
          <div className="search-input-wrap">
            <Search size={14} className="search-icon" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Keywords in description or notes"
            />
            {searchQuery && (
              <button
                type="button"
                className="search-clear"
                onClick={() => setSearchQuery('')}
                title="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
        <div className="filter-group">
          <label>Date Range</label>
          <select value={dateRange} onChange={e => setDateRange(e.target.value)}>
            <option value="this_month">This Month</option>
            <option value="last_month">Last Month</option>
            <option value="last_3_months">Last 3 Months</option>
            <option value="year_to_date">Year to Date</option>
            <option value="custom">Custom</option>
          </select>
        </div>
        {dateRange === 'custom' && (
          <>
            <div className="filter-group">
              <label>Start</label>
              <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} />
            </div>
            <div className="filter-group">
              <label>End</label>
              <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} />
            </div>
          </>
        )}
        <div className="filter-group">
          <label>Account</label>
          <select value={filterAccountId} onChange={e => setFilterAccountId(e.target.value)}>
            <option value="all">All Accounts</option>
            {activeAccounts.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
            <option value="">(no account)</option>
          </select>
        </div>
        {activeTab === 'expenses' && (
          <>
            <div className="filter-group">
              <label>Category</label>
              <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
                <option value="all">All Categories</option>
                {categoryOptions.map(o => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Status</label>
              <select value={filterPaid} onChange={e => setFilterPaid(e.target.value)}>
                <option value="all">All</option>
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </div>
          </>
        )}
        <div className="filter-group">
          <label>Reviewed</label>
          <select value={filterReviewed} onChange={e => setFilterReviewed(e.target.value)}>
            <option value="all">All</option>
            <option value="unreviewed">Not reviewed</option>
            <option value="reviewed">Reviewed</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Duplicates</label>
          <select value={filterDuplicates} onChange={e => setFilterDuplicates(e.target.value)}>
            <option value="all">Show all</option>
            <option value="only">Only duplicates</option>
          </select>
        </div>
      </div>

      {/* Expenses Table */}
      {activeTab === 'expenses' && (
        <div className="expenses-table-section">
          {expenseExactDupIdsToDelete.length > 0 && (
            <div className="dup-banner" style={{ justifyContent: 'space-between' }}>
              <span>
                <Copy size={14} /> {expenseExactDupIdsToDelete.length} exact duplicate{expenseExactDupIdsToDelete.length === 1 ? '' : 's'} found (same description, notes, amount, and due date)
              </span>
              <button
                type="button"
                className="btn-primary"
                onClick={() => handleRemoveExactDuplicates('expense')}
                disabled={removingDuplicates}
              >
                <Trash2 size={14} /> {removingDuplicates ? 'Removing…' : `Remove ${expenseExactDupIdsToDelete.length} duplicate${expenseExactDupIdsToDelete.length === 1 ? '' : 's'}`}
              </button>
            </div>
          )}
          {visibleExpenseDupCount > 0 && filterDuplicates !== 'only' && (
            <button
              type="button"
              className="dup-banner"
              onClick={() => setFilterDuplicates('only')}
            >
              <Copy size={14} /> {visibleExpenseDupCount} possible duplicate{visibleExpenseDupCount === 1 ? '' : 's'} — click to filter
            </button>
          )}
          {selectedExpenseCount > 0 && (
            <div className="bulk-action-bar">
              <div className="bulk-summary">
                <strong>{selectedExpenseCount}</strong> selected
                <button className="link-btn" onClick={() => setSelectedExpenseIds({})}>
                  Clear
                </button>
              </div>
              <div className="bulk-controls">
                <label>Assign category</label>
                <select
                  value={bulkCategoryId}
                  onChange={e => setBulkCategoryId(e.target.value)}
                  disabled={bulkApplying}
                >
                  <option value="">— Uncategorized —</option>
                  {categoryOptions.map(o => (
                    <option key={o.id} value={o.id}>{o.label}</option>
                  ))}
                </select>
                <button
                  className="btn-primary"
                  onClick={applyBulkExpenseCategory}
                  disabled={bulkApplying}
                >
                  {bulkApplying ? 'Applying…' : `Apply to ${selectedExpenseCount}`}
                </button>
              </div>
              {bulkError && (
                <div className="bulk-error"><AlertTriangle size={14} /> {bulkError}</div>
              )}
            </div>
          )}
          {expenses.length === 0 ? (
            <div className="expenses-empty">
              <DollarSign size={48} />
              <p>No expenses found for this period</p>
            </div>
          ) : (
            <table className="expenses-table">
              <thead>
                <tr>
                  <th className="cb-col">
                    <input
                      type="checkbox"
                      checked={allExpensesSelected}
                      onChange={toggleAllExpenses}
                      title={allExpensesSelected ? 'Deselect all' : 'Select all'}
                    />
                  </th>
                  <th>Description</th>
                  <th>Category</th>
                  <th>Account</th>
                  <th>Due Date</th>
                  <th className="right">Amount</th>
                  <th>Status</th>
                  <th>Reviewed</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map(exp => {
                  const isDup = expenseDuplicateIds.has(exp.id);
                  return (
                  <tr
                    key={exp.id}
                    className={`${exp.reviewed ? '' : 'row-unreviewed'} ${selectedExpenseIds[exp.id] ? 'row-selected' : ''} ${isDup ? 'row-duplicate' : ''}`}
                  >
                    <td className="cb-col">
                      <input
                        type="checkbox"
                        checked={!!selectedExpenseIds[exp.id]}
                        onChange={() => toggleExpenseSelection(exp.id)}
                      />
                    </td>
                    <td>
                      <div>
                        {exp.description}
                        {isDup && (
                          <span className="dup-badge" title="Same amount on the same account within a few days of another charge">
                            <Copy size={11} /> Possible duplicate
                          </span>
                        )}
                      </div>
                      {exp.notes && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{exp.notes}</div>}
                    </td>
                    <td><span className="account-badge">{getExpenseCategoryDisplay(exp, categoryLookup)}</span></td>
                    <td>{accountName(exp.accountId)}</td>
                    <td>{exp.dueDate}</td>
                    <td className="right amount expense">{formatCurrency(exp.amount)}</td>
                    <td>
                      {exp.paid ? (
                        <span className="status-badge paid"><Check size={12} /> Paid</span>
                      ) : isOverdue(exp) ? (
                        <span className="status-badge overdue"><AlertTriangle size={12} /> Overdue</span>
                      ) : (
                        <span className="status-badge unpaid">Unpaid</span>
                      )}
                    </td>
                    <td>
                      <button
                        className={`review-toggle ${exp.reviewed ? 'is-reviewed' : ''}`}
                        onClick={() => handleToggleReviewed(exp, 'expense')}
                        title={exp.reviewed ? 'Mark as not reviewed' : 'Mark as reviewed'}
                      >
                        {exp.reviewed ? <><Eye size={12} /> Reviewed</> : <><EyeOff size={12} /> Review</>}
                      </button>
                    </td>
                    <td>
                      <div className="row-actions">
                        {!exp.paid ? (
                          <button className="pay-btn" onClick={() => handleMarkPaid(exp)} title="Mark Paid">
                            <Check size={14} /> Pay
                          </button>
                        ) : (
                          <button onClick={() => handleMarkUnpaid(exp)} title="Mark Unpaid">
                            <X size={14} /> Undo
                          </button>
                        )}
                        <button onClick={() => openEditModal(exp, 'expense')} title="Edit">
                          <Pencil size={14} />
                        </button>
                        <button className="delete-btn" onClick={() => handleDelete(exp.id, 'expense')} title="Delete">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="5"><strong>Total</strong></td>
                  <td className="right amount expense">{formatCurrency(totalExpenses)}</td>
                  <td colSpan="3" />
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      )}

      {/* Deposits Table */}
      {activeTab === 'deposits' && (
        <div className="expenses-table-section">
          {depositExactDupIdsToDelete.length > 0 && (
            <div className="dup-banner" style={{ justifyContent: 'space-between' }}>
              <span>
                <Copy size={14} /> {depositExactDupIdsToDelete.length} exact duplicate{depositExactDupIdsToDelete.length === 1 ? '' : 's'} found (same description, notes, amount, and date)
              </span>
              <button
                type="button"
                className="btn-primary"
                onClick={() => handleRemoveExactDuplicates('deposit')}
                disabled={removingDuplicates}
              >
                <Trash2 size={14} /> {removingDuplicates ? 'Removing…' : `Remove ${depositExactDupIdsToDelete.length} duplicate${depositExactDupIdsToDelete.length === 1 ? '' : 's'}`}
              </button>
            </div>
          )}
          {visibleDepositDupCount > 0 && filterDuplicates !== 'only' && (
            <button
              type="button"
              className="dup-banner"
              onClick={() => setFilterDuplicates('only')}
            >
              <Copy size={14} /> {visibleDepositDupCount} possible duplicate{visibleDepositDupCount === 1 ? '' : 's'} — click to filter
            </button>
          )}
          {selectedDepositCount > 0 && (
            <div className="bulk-action-bar">
              <div className="bulk-summary">
                <strong>{selectedDepositCount}</strong> selected
                <button className="link-btn" onClick={() => setSelectedDepositIds({})}>
                  Clear
                </button>
              </div>
              <div className="bulk-controls">
                <label>Assign category</label>
                <select
                  value={bulkDepositCategory}
                  onChange={e => setBulkDepositCategory(e.target.value)}
                  disabled={bulkApplying}
                >
                  <option value="">— Choose category —</option>
                  {depositCategories.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
                <button
                  className="btn-primary"
                  onClick={applyBulkDepositCategory}
                  disabled={bulkApplying || !bulkDepositCategory}
                >
                  {bulkApplying ? 'Applying…' : `Apply to ${selectedDepositCount}`}
                </button>
              </div>
              {bulkError && (
                <div className="bulk-error"><AlertTriangle size={14} /> {bulkError}</div>
              )}
            </div>
          )}
          {deposits.length === 0 ? (
            <div className="expenses-empty">
              <TrendingUp size={48} />
              <p>No deposits or sales found for this period</p>
            </div>
          ) : (
            <table className="expenses-table">
              <thead>
                <tr>
                  <th className="cb-col">
                    <input
                      type="checkbox"
                      checked={allDepositsSelected}
                      onChange={toggleAllDeposits}
                      title={allDepositsSelected ? 'Deselect all' : 'Select all'}
                    />
                  </th>
                  <th>Description</th>
                  <th>Category</th>
                  <th>Account</th>
                  <th>Date</th>
                  <th className="right">Amount</th>
                  <th>Reviewed</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {deposits.map(dep => {
                  const isDup = depositDuplicateIds.has(dep.id);
                  return (
                  <tr
                    key={dep.id}
                    className={`${dep.reviewed ? '' : 'row-unreviewed'} ${selectedDepositIds[dep.id] ? 'row-selected' : ''} ${isDup ? 'row-duplicate' : ''}`}
                  >
                    <td className="cb-col">
                      <input
                        type="checkbox"
                        checked={!!selectedDepositIds[dep.id]}
                        onChange={() => toggleDepositSelection(dep.id)}
                      />
                    </td>
                    <td>
                      <div>
                        {dep.description}
                        {isDup && (
                          <span className="dup-badge" title="Same amount on the same account within a few days of another deposit">
                            <Copy size={11} /> Possible duplicate
                          </span>
                        )}
                      </div>
                      {dep.notes && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{dep.notes}</div>}
                    </td>
                    <td><span className="account-badge">{getDepositCategoryLabel(dep.category)}</span></td>
                    <td>{accountName(dep.accountId)}</td>
                    <td>{dep.date}</td>
                    <td className="right amount income">{formatCurrency(dep.amount)}</td>
                    <td>
                      <button
                        className={`review-toggle ${dep.reviewed ? 'is-reviewed' : ''}`}
                        onClick={() => handleToggleReviewed(dep, 'deposit')}
                        title={dep.reviewed ? 'Mark as not reviewed' : 'Mark as reviewed'}
                      >
                        {dep.reviewed ? <><Eye size={12} /> Reviewed</> : <><EyeOff size={12} /> Review</>}
                      </button>
                    </td>
                    <td>
                      <div className="row-actions">
                        <button onClick={() => openEditModal(dep, 'deposit')} title="Edit">
                          <Pencil size={14} />
                        </button>
                        <button className="delete-btn" onClick={() => handleDelete(dep.id, 'deposit')} title="Delete">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="4"><strong>Total</strong></td>
                  <td />
                  <td className="right amount income">{formatCurrency(totalDeposits)}</td>
                  <td colSpan="2" />
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      )}

      {/* Transfers Table */}
      {activeTab === 'transfers' && (
        <div className="expenses-table-section">
          <div style={{ padding: '12px 20px', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            Transfers move money between your own accounts. They don't affect income, expenses, or P&amp;L.
          </div>
          {visibleTransferDupCount > 0 && filterDuplicates !== 'only' && (
            <button
              type="button"
              className="dup-banner"
              onClick={() => setFilterDuplicates('only')}
            >
              <Copy size={14} /> {visibleTransferDupCount} possible duplicate{visibleTransferDupCount === 1 ? '' : 's'} — click to filter
            </button>
          )}
          {transfers.length === 0 ? (
            <div className="expenses-empty">
              <ArrowRightLeft size={48} />
              <p>No transfers in this period</p>
            </div>
          ) : (
            <table className="expenses-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Date</th>
                  <th className="right">Amount</th>
                  <th>Reviewed</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {transfers.map(tr => {
                  const isDup = transferDuplicateIds.has(tr.id);
                  return (
                  <tr
                    key={tr.id}
                    className={`${tr.reviewed ? '' : 'row-unreviewed'} ${isDup ? 'row-duplicate' : ''}`}
                  >
                    <td>
                      <div>
                        {tr.description || <span style={{ color: 'var(--text-muted)' }}>Transfer</span>}
                        {isDup && (
                          <span className="dup-badge" title="Same amount between the same accounts within a few days of another transfer">
                            <Copy size={11} /> Possible duplicate
                          </span>
                        )}
                      </div>
                      {tr.notes && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{tr.notes}</div>}
                    </td>
                    <td>{accountName(tr.fromAccountId)}</td>
                    <td>{accountName(tr.toAccountId)}</td>
                    <td>{tr.date}</td>
                    <td className="right amount">{formatCurrency(tr.amount)}</td>
                    <td>
                      <button
                        className={`review-toggle ${tr.reviewed ? 'is-reviewed' : ''}`}
                        onClick={() => handleToggleReviewed(tr, 'transfer')}
                        title={tr.reviewed ? 'Mark as not reviewed' : 'Mark as reviewed'}
                      >
                        {tr.reviewed ? <><Eye size={12} /> Reviewed</> : <><EyeOff size={12} /> Review</>}
                      </button>
                    </td>
                    <td>
                      <div className="row-actions">
                        <button onClick={() => openEditModal(tr, 'transfer')} title="Edit">
                          <Pencil size={14} />
                        </button>
                        <button className="delete-btn" onClick={() => handleDelete(tr.id, 'transfer')} title="Delete">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="4"><strong>Total transferred</strong></td>
                  <td className="right amount">{formatCurrency(totalTransfers)}</td>
                  <td colSpan="2" />
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {editingItem ? 'Edit' : 'Add'}{' '}
                {modalType === 'expense' ? 'Expense'
                  : modalType === 'transfer' ? 'Transfer'
                  : 'Deposit / Sale'}
              </h2>
              <button className="modal-close" onClick={closeModal}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>
                  {modalType === 'transfer' ? 'Description (optional)' : 'Description *'}
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  placeholder={
                    modalType === 'expense' ? 'e.g. Cleaning supplies from Costco'
                      : modalType === 'transfer' ? 'e.g. Move funds to savings'
                      : 'e.g. Deep clean - Smith residence'
                  }
                />
              </div>
              {modalType === 'transfer' && (
                <div className="form-row">
                  <div className="form-group">
                    <label>From Account *</label>
                    <select
                      value={formData.fromAccountId || ''}
                      onChange={e => setFormData({ ...formData, fromAccountId: e.target.value })}
                    >
                      <option value="">— Choose source —</option>
                      {activeAccounts.map(a => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>To Account *</label>
                    <select
                      value={formData.toAccountId || ''}
                      onChange={e => setFormData({ ...formData, toAccountId: e.target.value })}
                    >
                      <option value="">— Choose destination —</option>
                      {activeAccounts.map(a => (
                        <option
                          key={a.id}
                          value={a.id}
                          disabled={a.id === formData.fromAccountId}
                        >
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label>Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount}
                    onChange={e => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                {modalType === 'transfer' ? (
                  <div className="form-group">
                    <label>Date</label>
                    <input
                      type="date"
                      value={formData.date || ''}
                      onChange={e => setFormData({ ...formData, date: e.target.value })}
                    />
                  </div>
                ) : (
                <div className="form-group">
                  <label>{modalType === 'expense' ? 'Category' : 'Category'}</label>
                  {modalType === 'expense' ? (
                    <>
                      <select
                        value={formData.categoryId || ''}
                        onChange={e => setFormData({ ...formData, categoryId: e.target.value })}
                      >
                        <option value="">— Uncategorized —</option>
                        {categoryOptions.map(o => (
                          <option key={o.id} value={o.id}>{o.label}</option>
                        ))}
                      </select>
                      {categories.length === 0 && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                          No categories yet. <Link to="/admin/expense-categories">Add categories</Link>
                        </div>
                      )}
                    </>
                  ) : (
                    <select
                      value={formData.category}
                      onChange={e => setFormData({ ...formData, category: e.target.value })}
                    >
                      {depositCategories.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  )}
                </div>
                )}
              </div>
              {modalType !== 'transfer' && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Account</label>
                    <select
                      value={formData.accountId || ''}
                      onChange={e => setFormData({ ...formData, accountId: e.target.value })}
                    >
                      <option value="">— None —</option>
                      {activeAccounts.map(a => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                      ))}
                    </select>
                    {activeAccounts.length === 0 && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                        No accounts yet. <Link to="/admin/accounts">Add accounts</Link>
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>{modalType === 'expense' ? 'Due Date' : 'Date'}</label>
                    <input
                      type="date"
                      value={modalType === 'expense' ? (formData.dueDate || '') : (formData.date || '')}
                      onChange={e => setFormData({
                        ...formData,
                        ...(modalType === 'expense' ? { dueDate: e.target.value } : { date: e.target.value })
                      })}
                    />
                  </div>
                </div>
              )}
              {modalType === 'expense' && (
                <div className="form-checkbox">
                  <input
                    type="checkbox"
                    id="paid-check"
                    checked={!!formData.paid}
                    onChange={e => setFormData({ ...formData, paid: e.target.checked })}
                  />
                  <label htmlFor="paid-check">Already paid</label>
                </div>
              )}
              <div className="form-group">
                <label>Notes (optional)</label>
                <textarea
                  value={formData.notes || ''}
                  onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any additional details..."
                />
              </div>
            </div>
            {saveError && (
              <div className="modal-error" role="alert">
                <AlertTriangle size={16} /> {saveError}
              </div>
            )}
            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeModal} disabled={saving}>Cancel</button>
              <button className="btn-save" onClick={handleSave} disabled={saving}>
                {saving
                  ? 'Saving…'
                  : editingItem
                    ? 'Save Changes'
                    : modalType === 'expense' ? 'Add Expense'
                      : modalType === 'transfer' ? 'Record Transfer'
                        : 'Add Deposit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Expenses;
